import { memo, useCallback, useMemo, useRef, useState, type FunctionComponent } from "react";
import { CopyOutlined, DeleteOutlined, PlusOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { Badge, Button, Card, Dropdown, Empty, Form, message, Space, Table, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import keyBy from "lodash.keyby";
import cloneDeep from "lodash.clonedeep";
import groupBy from "lodash.groupby";
import last from "lodash.last";
import omit from "lodash.omit";
import sortBy from "lodash.sortby";
import { Utensils } from "lucide-react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { automationTemplatesActions, programAutomationActions, RequestStatus } from "@fitness-app/app-store";
import { ProgramService } from "@fitness-app/data-models/domain/services/ProgramService";
import {
  EmailClientTask,
  TaskCreator,
  TaskType,
  type AutomationTask,
  type FieldValuesEmailClient,
  type FieldValuesEmailList,
  type TagsEmailClient,
  type TagsEmailList,
} from "@fitness-app/data-models/entities/Automation";
import {
  AI_AUTOMATION_TASK_LIMITS,
  AutomationMessageType,
  DEFAULT_AI_AUTOMATION_TASK_SENDER,
  TemplateType,
  type ScheduledMessagesAutomation,
  type ScheduledMessagesTemplate,
  type ScheduledSurveyAutomation,
  type ScheduledSurveyAutomationPlanned,
} from "@fitness-app/data-models/entities/AutomationTemplate";
import { type FileMessageContent } from "@fitness-app/data-models/entities/Chat";
import { mediaLibraryVideoToAttachment } from "@fitness-app/data-models/entities/ChatAttachment";
import { CycleSurveyType, SurveyType } from "@fitness-app/data-models/entities/ClientSurvey";
import { EmailClient } from "@fitness-app/data-models/entities/ProductsSettings";
import {
  isWeekInAiWindow,
  ProgramAutomationScheduleAnchor,
  ScheduledNutritionType,
  toAiNutritionPlans,
  toAiTrainingPrograms,
  type DayContent,
  type ProgramAutomationScheduledTask,
  type ProgramAutomationTaskScheduleTiming,
  type WeekdayNumberToString,
  type WeekdaysNumber,
} from "@fitness-app/data-models/entities/ProgramAutomation";
import { type ProgramWorkout, type WeekDay } from "@fitness-app/data-models/entities/TrainingProgram";

import ModalForm from "~/components/ModalForm/ModalForm";
import { LIST_ITEM_SEPARATOR } from "~/constants/separators";
import { useAuthorOptions } from "~/hooks/trainer/useAuthorOptions";
import { useLoading } from "~/hooks/useLoading";
import AutomatedMessagesForm from "~/modules/Automation/AutomatedMessagesForm/AutomatedMessagesForm";
import { FormMode, type AutomatedMessagesFormModel } from "~/modules/Automation/AutomatedMessagesForm/types";
import AddAutomationButton from "~/modules/Automation/ProgramAutomation/components/AddAutomationButton/AddAutomationButton";
import AiAgentMessageItem from "~/modules/Automation/ProgramAutomation/components/AiAgentMessageItem/AiAgentMessageItem";
import AiAgentMessageTaskForm from "~/modules/Automation/ProgramAutomation/components/AiAgentMessageTaskForm/AiAgentMessageTaskForm";
import { type AiAgentMessageTaskFormModel } from "~/modules/Automation/ProgramAutomation/components/AiAgentMessageTaskForm/types";
import { useAiAgentMessageTaskForm } from "~/modules/Automation/ProgramAutomation/components/AiAgentMessageTaskForm/useAiAgentMessageTaskForm";
import AiReportItem from "~/modules/Automation/ProgramAutomation/components/AiReportItem/AiReportItem";
import AiReportTaskForm from "~/modules/Automation/ProgramAutomation/components/AiReportTaskForm/AiReportTaskForm";
import { type AiReportTaskFormModel } from "~/modules/Automation/ProgramAutomation/components/AiReportTaskForm/types";
import { useAiReportTaskForm } from "~/modules/Automation/ProgramAutomation/components/AiReportTaskForm/useAiReportTaskForm";
import AssignHabitsItem from "~/modules/Automation/ProgramAutomation/components/AssignHabitsItem/AssignHabitsItem";
import AssignHabitsTaskForm from "~/modules/Automation/ProgramAutomation/components/AssignHabitsTaskForm/AssignHabitsTaskForm";
import { type AssignHabitsTaskFormModel } from "~/modules/Automation/ProgramAutomation/components/AssignHabitsTaskForm/types";
import { useAssignHabitsTaskForm } from "~/modules/Automation/ProgramAutomation/components/AssignHabitsTaskForm/useAssignHabitsTaskForm";
import EnrollChallengeItem from "~/modules/Automation/ProgramAutomation/components/EnrollChallengeItem/EnrollChallengeItem";
import EnrollChallengeTaskForm from "~/modules/Automation/ProgramAutomation/components/EnrollChallengeTaskForm/EnrollChallengeTaskForm";
import { type EnrollChallengeTaskFormModel } from "~/modules/Automation/ProgramAutomation/components/EnrollChallengeTaskForm/types";
import { useEnrollChallengeTaskForm } from "~/modules/Automation/ProgramAutomation/components/EnrollChallengeTaskForm/useEnrollChallengeTaskForm";
import AddSurveyTemplateForm from "~/modules/Automation/ProgramAutomation/components/AddSurveyTemplateForm/AddSurveyTemplateForm";
import { SurveyAutomationItem } from "~/modules/Automation/ProgramAutomation/components/AddSurveyTemplateForm/SurveyAutomationItem";
import EmailClientTaskItem from "~/modules/Automation/ProgramAutomation/components/EmailClientTaskItem/EmailClientTaskItem";
import FitnessAppTaskForm from "~/modules/Automation/ProgramAutomation/components/FitnessAppTaskForm/FitnessAppTaskForm";
import { type FitnessAppTaskFormModel } from "~/modules/Automation/ProgramAutomation/components/FitnessAppTaskForm/types";
import { useFitnessAppTaskForm } from "~/modules/Automation/ProgramAutomation/components/FitnessAppTaskForm/useFitnessAppTaskForm";
import FitnessAppTaskItem from "~/modules/Automation/ProgramAutomation/components/FitnessAppTaskItem/FitnessAppTaskItem";
import { useCampaignPopupForm } from "~/modules/Automation/ProgramAutomation/components/hooks/useCampaignPopupForm";
import { useSurveyTemplateForm } from "~/modules/Automation/ProgramAutomation/components/hooks/useSurveyTemplateForm";
import MessageAutomationItem from "~/modules/Automation/ProgramAutomation/components/MessageAutomationItem/MessageAutomationItem";
import PopUpAutomationItem from "~/modules/Automation/ProgramAutomation/components/PopUpAutomationItem/PopUpAutomationItem";
import ProgramAutomationTaskForm from "~/modules/Automation/ProgramAutomation/components/ProgramAutomationTaskForm/AutomationTaskForm";
import { type ProgramAutomationTaskFormModel } from "~/modules/Automation/ProgramAutomation/components/ProgramAutomationTaskForm/types";
import { useProgramAutomationTaskForm } from "~/modules/Automation/ProgramAutomation/components/ProgramAutomationTaskForm/useProgramAutomationTaskForm";
import { toMessageAttachmentsFormValues } from "~/modules/Chat/components/MessageAttachments/MessageAttachmentsFormItems";
import TeamMemberTaskForm from "~/modules/Automation/ProgramAutomation/components/TeamMemberTaskForm/TeamMemberTaskForm";
import { type TeamMemberTaskFormModel } from "~/modules/Automation/ProgramAutomation/components/TeamMemberTaskForm/types";
import { useTeamMemberTaskForm } from "~/modules/Automation/ProgramAutomation/components/TeamMemberTaskForm/useTeamMemberTaskForm";
import TeamMemberTaskItem from "~/modules/Automation/ProgramAutomation/components/TeamMemberTaskItem/TeamMemberTaskItem";
import {
  getScheduleTimingLabel,
  isPackageAnchoredTiming,
} from "~/modules/Automation/ProgramAutomation/components/ScheduleTimingFields/scheduleTimingLabel";
import { createAutomationWeekTemplate } from "~/modules/Automation/ProgramAutomation/helpers";
import { type AutomationRow } from "~/modules/Automation/ProgramAutomation/ProgramAutomationDetails";
import ComposeCampaignForm from "~/modules/Campaigns/CampaignForm/ComposeCampaignForm";
import { type CampaignMessageComposeFormModel } from "~/modules/Campaigns/CampaignForm/types";
import { analyticsService, useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { transformFromFiltersToFormData, transformFromFormDataToFilters } from "../FiltersField/FiltersField";

const weekdays = Array.from({ length: 7 });
const weekdaysArr = ["", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

// forms opened from the package-tasks card start with a package anchor preselected
const PACKAGE_TASK_PRESET: { scheduleTiming: ProgramAutomationTaskScheduleTiming } = {
  scheduleTiming: { anchor: ProgramAutomationScheduleAnchor.PackageStart, offsetDays: 0 },
};

// a task submitted with the week/day anchor but no source cell lands in week 1 / day 1
const DEFAULT_CELL = { week: "1", day: "1" as WeekdayNumberToString };

const aiWeeksLabel = (config: { startWeek: number; endWeek: number | null }) =>
  config.endWeek ? `tydz. ${config.startWeek}–${config.endWeek}` : `od tydz. ${config.startWeek} do końca`;

/**
 * TRE-1707 — AI plans/diets are configured on the `aiTrainingProgram` / `aiNutritionPlan` columns,
 * not in `scheduledWorkoutsRoutine` / `scheduledNutritionPlan`, so the week grid never saw them.
 * The marker is the AI counterpart of the workout/nutrition badges: the exact days aren't known
 * up front (the agent generates them per client), so it sits in the day-1 cell of a covered week.
 */
const AiPlanMarker: FunctionComponent<{ label: string; tooltip: string; muted?: boolean }> = ({
  label,
  tooltip,
  muted,
}) => (
  <Tooltip title={tooltip}>
    <span className={muted ? "text-gray-400" : "text-violet-600"}>
      <ThunderboltOutlined /> <span>{label}</span>
    </span>
  </Tooltip>
);

const AutomationScheduleTable = () => {
  const { t } = useTranslation(["automation", "common"]);
  const {
    visible: automationTaskFormVisible,
    closeModal: closeTaskModal,
    openModal: openTaskModal,
    programAutomationTaskFormController,
    editedTask,
  } = useProgramAutomationTaskForm();
  const {
    visible: fitnessAppTaskFormVisible,
    closeModal: closeFitnessAppTaskModal,
    openModal: openFitnessAppTaskModal,
    formController: fitnessAppTaskFormController,
  } = useFitnessAppTaskForm();
  const {
    visible: teamMemberTaskFormVisible,
    closeModal: closeTeamMemberTaskModal,
    openModal: openTeamMemberTaskModal,
    formController: teamMemberTaskFormController,
  } = useTeamMemberTaskForm();
  const {
    visible: aiAgentMessageFormVisible,
    closeModal: closeAiAgentMessageModal,
    openModal: openAiAgentMessageModal,
    formController: aiAgentMessageFormController,
    editedTask: editedAiAgentMessage,
  } = useAiAgentMessageTaskForm();
  const {
    visible: aiReportFormVisible,
    closeModal: closeAiReportModal,
    openModal: openAiReportModal,
    formController: aiReportFormController,
    editedTask: editedAiReport,
  } = useAiReportTaskForm();
  const {
    visible: assignHabitsFormVisible,
    closeModal: closeAssignHabitsModal,
    openModal: openAssignHabitsModal,
    formController: assignHabitsFormController,
    editedTask: editedAssignHabits,
  } = useAssignHabitsTaskForm();
  const {
    visible: enrollChallengeFormVisible,
    closeModal: closeEnrollChallengeModal,
    openModal: openEnrollChallengeModal,
    formController: enrollChallengeFormController,
    editedTask: editedEnrollChallenge,
  } = useEnrollChallengeTaskForm();
  const [loading, startLoading, stopLoading] = useLoading();
  const [selectedCell, setCell] = useState<null | {
    week: string;
    day: WeekdayNumberToString;
  }>(null);
  const [model, setModel] = useState<null | AutomatedMessagesFormModel>(null);
  const [showAutomatedMessagesForm, toggleAutomatedMessagesForm] = useState(false);
  const [campaignFormController] = Form.useForm<CampaignMessageComposeFormModel>();
  const { selectedAutomation, itemStatus } = useAppSelector((store) => store.programAutomation);
  const mediaLibrary = useAppSelector((store) => store.mediaLibrary.list);
  const editedTaskAttachments = useRef<FileMessageContent[]>([]);
  const { showSurveyForm, toggleSurveyForm, onSendSurveyForm } = useSurveyTemplateForm(selectedCell, () =>
    setCell(null),
  );
  const {
    showCampaignPopupForm,
    toggleCampaignPopupForm,
    handleCampaignSubmit,
    loading: campaignLoading,
    model: campaignModel,
    setEditedModelForCampaignPopup,
  } = useCampaignPopupForm(selectedCell, () => {
    setCell(null);
    campaignFormController.resetFields();
  });

  const dispatch = useAppDispatch();
  const authorOptions = useAuthorOptions(undefined, false, false);

  const handleSubmit = async ({
    sentTimeConfig,
    attachments: uploadedAttachments,
    mediaLibraryVideoId,
    ...formData
  }: AutomatedMessagesFormModel) => {
    if (!selectedAutomation) {
      return;
    }

    // The library video is stored as a ready attachment: nothing resolves it later, the
    // scheduled task copies the item as it is (TRE-2306).
    const libraryVideo = mediaLibrary.find((item) => item.id === mediaLibraryVideoId);
    // Falls back to the saved copy when the library list has not loaded yet.
    const videoAttachment = libraryVideo
      ? mediaLibraryVideoToAttachment(libraryVideo)
      : (editedTaskAttachments.current.find((file) => file.uid === mediaLibraryVideoId) ?? null);
    const attachments = [...(uploadedAttachments ?? []), ...(videoAttachment ? [videoAttachment] : [])];

    startLoading();

    const updatedModel: ProgramAutomationScheduledTask = model
      ? {
          ...model,
          ...formData,
          id: model.id || uuid(),
          filters: transformFromFormDataToFilters(formData.filters),
          attachments,
          type: TemplateType.ScheduledMessages,
          templateId: formData.id || null,
          sentTime: {
            hours: formData.sentTime.get("hour"),
            minutes: formData.sentTime.get("minutes"),
            mode: sentTimeConfig?.mode ?? "absolute",
            avoidSendingOnTheNight: sentTimeConfig?.avoidSendingOnTheNight ?? true,
            calculateFullRangeOverNight: sentTimeConfig?.calculateFullRangeOverNight ?? false,
          },
          scheduleTiming: formData.scheduleTiming ?? null,
        }
      : {
          ...formData,
          attachments,
          filters: transformFromFormDataToFilters(formData.filters),
          id: uuid(),
          type: TemplateType.ScheduledMessages,
          templateId: formData.id || null,
          sentTime: {
            hours: formData.sentTime.get("hour"),
            minutes: formData.sentTime.get("minutes"),
            mode: sentTimeConfig?.mode ?? "absolute",
            avoidSendingOnTheNight: sentTimeConfig?.avoidSendingOnTheNight ?? true,
            calculateFullRangeOverNight: sentTimeConfig?.calculateFullRangeOverNight ?? false,
          },
          scheduleTiming: formData.scheduleTiming ?? null,
        };

    const bag = selectedAutomation.packageAnchoredTasks ?? [];
    const wasInBag = model ? bag.some((task) => task.id === updatedModel.id) : false;
    const isPackageAnchored = isPackageAnchoredTiming(updatedModel.scheduleTiming);

    const automationUpdate: Parameters<typeof programAutomationActions.updateAutomationTemplate>[0]["automation"] = {
      id: selectedAutomation.id,
    };

    if (isPackageAnchored) {
      automationUpdate.packageAnchoredTasks = wasInBag
        ? bag.map((task) => (task.id === updatedModel.id ? updatedModel : task))
        : [...bag, updatedModel];

      // a grid task edited into a package anchor moves out of its cell
      if (model && !wasInBag && selectedCell) {
        const weekToUpdate = cloneDeep(selectedAutomation.schedule[selectedCell.week]) || {};
        weekToUpdate[selectedCell.day] = {
          id: weekToUpdate[selectedCell.day]?.id ?? uuid(),
          items: weekToUpdate[selectedCell.day]?.items.filter((item) => item.id !== updatedModel.id) ?? [],
        };
        automationUpdate.schedule = {
          ...selectedAutomation.schedule,
          [selectedCell.week]: weekToUpdate,
        };
      }
    } else {
      // a bag task switched back to the week/day anchor lands in week 1 / day 1
      const cell = selectedCell ?? DEFAULT_CELL;
      const weekToUpdate = cloneDeep(selectedAutomation.schedule[cell.week]) || {};

      if (model && !wasInBag) {
        weekToUpdate[cell.day] = {
          id: weekToUpdate[cell.day]?.id ?? "",
          items: weekToUpdate[cell.day]?.items.map((item) => (item.id !== updatedModel.id ? item : updatedModel)) ?? [],
        };
      } else {
        const itemsToUpdate = weekToUpdate[cell.day];
        weekToUpdate[cell.day] = {
          id: weekToUpdate[cell.day]?.id ?? uuid(),
          items: itemsToUpdate?.items ? [...itemsToUpdate.items, updatedModel] : [updatedModel],
        };
      }

      automationUpdate.schedule = {
        ...selectedAutomation.schedule,
        [cell.week]: weekToUpdate,
      };

      if (wasInBag) {
        automationUpdate.packageAnchoredTasks = bag.filter((task) => task.id !== updatedModel.id);
      }
    }

    try {
      await dispatch(
        programAutomationActions.updateAutomationTemplate({
          automationId: selectedAutomation.id,
          automation: automationUpdate,
        }),
      ).unwrap();
      if (formData.saveAsTemplate) {
        const payload: ScheduledMessagesTemplate = {
          id: uuid(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          name: formData.name,
          comment: formData.comment || "",
          type: TemplateType.ScheduledMessages,
          data: {
            attachments,
            messages: formData.messages,
            authorId: formData.authorId,
            authorRole: formData.authorRole,
            authorName: formData.authorName,
            sentTime: {
              hours: formData.sentTime.get("hour"),
              minutes: formData.sentTime.get("minutes"),
            },
          },
        };
        await dispatch(automationTemplatesActions.addTemplate(payload));
      }
      stopLoading();
      toggleAutomatedMessagesForm(false);
      setModel(null);
      setCell(null);
    } catch {
      stopLoading();
      void message.error(t("saveError"));
    }
  };

  const setEditedModelForAutomatedMessages = (
    task: ScheduledMessagesAutomation & Pick<ProgramAutomationScheduledTask, "scheduleTiming">,
    cell: { day: WeekdayNumberToString; week: string } | null,
  ) => {
    setCell(cell);
    editedTaskAttachments.current = task.attachments ?? [];
    setModel({
      ...task,
      scheduleTiming: task.scheduleTiming ?? { anchor: ProgramAutomationScheduleAnchor.WeekDay },
      comment: task.comment ?? "",
      sentTime: dayjs().set("hour", task.sentTime.hours).set("minutes", task.sentTime.minutes),
      mode: FormMode.Template,
      messageType: task.messageType ?? AutomationMessageType.OneTime,
      filters: transformFromFiltersToFormData(task.filters),
      ...toMessageAttachmentsFormValues(task.attachments),
      sentTimeConfig: {
        mode: task.sentTime?.mode ?? "absolute",
        avoidSendingOnTheNight: task.sentTime?.avoidSendingOnTheNight ?? false,
        calculateFullRangeOverNight: task.sentTime?.calculateFullRangeOverNight ?? false,
      },
    } as AutomatedMessagesFormModel);
    toggleAutomatedMessagesForm(true);
  };

  // A new package-anchored task goes to the bag; a week/day one goes to the cell it was added from.
  const createAutomationUpdateForNewTask = (
    automationTask: ProgramAutomationScheduledTask,
    cell: { week: string; day: WeekdayNumberToString },
  ): Parameters<typeof programAutomationActions.updateAutomationTemplate>[0]["automation"] => {
    if (!selectedAutomation) {
      return {};
    }

    if (isPackageAnchoredTiming(automationTask.scheduleTiming)) {
      return {
        packageAnchoredTasks: [...(selectedAutomation.packageAnchoredTasks ?? []), automationTask],
      };
    }

    const weekToUpdate = cloneDeep(selectedAutomation.schedule[cell.week]) || {};
    weekToUpdate[cell.day] = {
      id: weekToUpdate[cell.day]?.id ?? uuid(),
      items: [...(weekToUpdate[cell.day]?.items || []), automationTask],
    };

    return {
      schedule: {
        ...selectedAutomation.schedule,
        [cell.week]: weekToUpdate,
      },
    };
  };

  const handleSubmitEmailTask = (formData: ProgramAutomationTaskFormModel) => {
    if (!selectedAutomation) {
      return;
    }

    let action: AutomationTask["action"] | null = null;
    let automationTask: ProgramAutomationScheduledTask | null = null;

    const taskBaseData = {
      active: formData.active,
      description: formData.description,
      id: uuid(),
      protected: formData.protected ?? false,
      creator: formData.creator ?? TaskCreator.Owner,
      sentTime: {
        hours: formData.sentTime.get("hour"),
        minutes: formData.sentTime.get("minutes"),
        mode: formData.sentTimeConfig?.mode ?? "absolute",
        avoidSendingOnTheNight: formData.sentTimeConfig?.avoidSendingOnTheNight ?? true,
        calculateFullRangeOverNight: formData.sentTimeConfig?.calculateFullRangeOverNight ?? false,
      },
      scheduleTiming: formData.scheduleTiming ?? null,
    };

    if (formData.action.type === EmailClientTask.AddTags || formData.action.type === EmailClientTask.RemoveTags) {
      const lists: (TagsEmailList | TagsEmailClient)[] = formData.action.emailClients.map((listStr) => {
        const [client, account, id, name] = listStr.split(LIST_ITEM_SEPARATOR);
        if (id && name) {
          return {
            client: client as EmailClient,
            account,
            id,
            name,
          } as TagsEmailList;
        }
        return {
          client: client as EmailClient,
          account,
        } as TagsEmailClient;
      });
      const groupByClient = groupBy(lists, "client");

      const getResponsesAccounts = groupByClient[EmailClient.GetResponse];

      if (getResponsesAccounts && getResponsesAccounts.length > 1) {
        void message.warning("getResponseListWarning");
        return;
      }

      action = {
        ...formData.action,
        emailClients: lists,
      };

      automationTask = {
        ...taskBaseData,
        action,
        type: TaskType.EmailClient,
      };
    }

    if (
      formData.action.type === EmailClientTask.AddToLists ||
      formData.action.type === EmailClientTask.RemoveFromLists
    ) {
      const lists = formData.action.emailLists.map((listStr) => {
        const [client, account, id, name] = listStr.split(LIST_ITEM_SEPARATOR);
        return {
          client: client as EmailClient,
          account: account || "",
          id: id || "",
          name: name || "",
        };
      });
      const groupByClient = groupBy(lists, "client");

      const getResponsesAccounts = groupByClient[EmailClient.GetResponse];

      if (getResponsesAccounts && getResponsesAccounts.length > 1) {
        void message.warning("getResponseListWarning");
        return;
      }

      action = {
        ...formData.action,
        emailLists: lists,
      };

      automationTask = {
        ...taskBaseData,
        action,
        type: TaskType.EmailClient,
      };
    }

    if (formData.action.type === EmailClientTask.AddFieldValues) {
      const lists: (FieldValuesEmailList | FieldValuesEmailClient)[] = formData.action.emailLists.map((listStr) => {
        const [client, account, id, name] = listStr.split(LIST_ITEM_SEPARATOR);
        if (id && name) {
          return {
            client: client as EmailClient,
            account,
            id,
            name,
          } as FieldValuesEmailList;
        }
        return {
          client: client as EmailClient,
          account,
        } as FieldValuesEmailClient;
      });
      const groupByClient = groupBy(lists, "client");

      const getResponsesAccounts = groupByClient[EmailClient.GetResponse];

      if (getResponsesAccounts && getResponsesAccounts.length > 1) {
        void message.warning("getResponseListWarning");
        return;
      }

      action = {
        ...formData.action,
        emailLists: lists,
        values: formData.action.values
          ? Object.fromEntries(formData.action.values?.map((item) => [item.key, item.value]))
          : {},
      };

      automationTask = {
        ...taskBaseData,
        action,
        type: TaskType.EmailClient,
      };
    }
    if (!automationTask) {
      return null;
    }

    void dispatch(
      programAutomationActions.updateAutomationTemplate({
        automationId: selectedAutomation.id,
        automation: createAutomationUpdateForNewTask(automationTask, selectedCell ?? DEFAULT_CELL),
      }),
    );

    toggleAutomatedMessagesForm(false);
    setCell(null);
    closeTaskModal();
  };

  const handleSubmitTagsAction = (formData: FitnessAppTaskFormModel) => {
    if (!selectedAutomation) {
      return;
    }

    const automationTask: ProgramAutomationScheduledTask = {
      id: uuid(),
      type: TaskType.FitnessApp,
      action: formData.action,
      sentTime: {
        hours: formData.sentTime.get("hour"),
        minutes: formData.sentTime.get("minutes"),
        mode: formData.sentTimeConfig?.mode ?? "absolute",
        avoidSendingOnTheNight: formData.sentTimeConfig?.avoidSendingOnTheNight ?? true,
        calculateFullRangeOverNight: formData.sentTimeConfig?.calculateFullRangeOverNight ?? false,
      },
      scheduleTiming: formData.scheduleTiming ?? null,
      description: formData.description,
    };

    void dispatch(
      programAutomationActions.updateAutomationTemplate({
        automationId: selectedAutomation.id,
        automation: createAutomationUpdateForNewTask(automationTask, selectedCell ?? DEFAULT_CELL),
      }),
    );

    setCell(null);
    closeFitnessAppTaskModal();
  };

  const handleSubmitTeamMemberTask = (formData: TeamMemberTaskFormModel) => {
    if (!selectedAutomation) {
      return;
    }

    const selectedTeamMember = authorOptions.find((author) => author.value === formData.assignedTeamMemberId);

    const automationTask: ProgramAutomationScheduledTask = {
      id: uuid(),
      type: TaskType.TeamMemberTask,
      title: formData.title,
      description: formData.description,
      priority: formData.priority,
      category: formData.category,
      assignedTeamMemberId: formData.assignedTeamMemberId,
      assignedTeamMemberName: selectedTeamMember?.label ?? "",
      assignedTeamMemberAvatar: selectedTeamMember?.avatarUrl ?? null,
      sentTime: {
        hours: formData.sentTime.get("hour"),
        minutes: formData.sentTime.get("minutes"),
        mode: formData.sentTimeConfig?.mode ?? "absolute",
        avoidSendingOnTheNight: formData.sentTimeConfig?.avoidSendingOnTheNight ?? true,
        calculateFullRangeOverNight: formData.sentTimeConfig?.calculateFullRangeOverNight ?? false,
      },
      scheduleTiming: formData.scheduleTiming ?? null,
    };

    void dispatch(
      programAutomationActions.updateAutomationTemplate({
        automationId: selectedAutomation.id,
        automation: createAutomationUpdateForNewTask(automationTask, selectedCell ?? DEFAULT_CELL),
      }),
    );

    setCell(null);
    closeTeamMemberTaskModal();
  };

  // Placement logic shared by the three task types that gained package anchoring
  // in TRE-1612 (AI advisor message / assign habits / enroll challenge). A package
  // anchor routes the task to the packageAnchoredTasks bag; anything else to a
  // week/day cell (defaulting to week 1 / day 1 when the form was opened without a
  // source cell). Mirrors handleSubmit for scheduled messages.
  const buildScheduledTaskUpdate = (
    task: ProgramAutomationScheduledTask,
    isEdit: boolean,
  ): Parameters<typeof programAutomationActions.updateAutomationTemplate>[0]["automation"] | null => {
    if (!selectedAutomation) {
      return null;
    }

    const bag = selectedAutomation.packageAnchoredTasks ?? [];
    const wasInBag = isEdit ? bag.some((item) => item.id === task.id) : false;

    if (isPackageAnchoredTiming(task.scheduleTiming)) {
      const automation: Parameters<typeof programAutomationActions.updateAutomationTemplate>[0]["automation"] = {
        packageAnchoredTasks: wasInBag ? bag.map((item) => (item.id === task.id ? task : item)) : [...bag, task],
      };

      // a grid task edited into a package anchor moves out of its source cell
      if (isEdit && !wasInBag && selectedCell) {
        const weekToUpdate = cloneDeep(selectedAutomation.schedule[selectedCell.week]) || {};
        weekToUpdate[selectedCell.day] = {
          id: weekToUpdate[selectedCell.day]?.id ?? uuid(),
          items: weekToUpdate[selectedCell.day]?.items.filter((item) => item.id !== task.id) ?? [],
        };
        automation.schedule = { ...selectedAutomation.schedule, [selectedCell.week]: weekToUpdate };
      }

      return automation;
    }

    const cell = selectedCell ?? DEFAULT_CELL;
    const weekToUpdate = cloneDeep(selectedAutomation.schedule[cell.week]) || {};

    if (isEdit && !wasInBag) {
      weekToUpdate[cell.day] = {
        id: weekToUpdate[cell.day]?.id ?? uuid(),
        items: weekToUpdate[cell.day]?.items.map((item) => (item.id === task.id ? task : item)) ?? [task],
      };
    } else {
      const itemsToUpdate = weekToUpdate[cell.day];
      weekToUpdate[cell.day] = {
        id: weekToUpdate[cell.day]?.id ?? uuid(),
        items: itemsToUpdate?.items ? [...itemsToUpdate.items, task] : [task],
      };
    }

    return {
      schedule: { ...selectedAutomation.schedule, [cell.week]: weekToUpdate },
      // a bag task switched back to a week/day anchor leaves the bag
      ...(wasInBag ? { packageAnchoredTasks: bag.filter((item) => item.id !== task.id) } : {}),
    };
  };

  const aiAgentMessageFormModel = useMemo<AiAgentMessageTaskFormModel | null>(
    () =>
      editedAiAgentMessage
        ? {
            name: editedAiAgentMessage.name,
            promptTemplate: editedAiAgentMessage.promptTemplate,
            tone: editedAiAgentMessage.tone ?? "",
            maxLength: editedAiAgentMessage.maxLength ?? AI_AUTOMATION_TASK_LIMITS.maxLength.default,
            upsellHint: editedAiAgentMessage.upsellHint ?? false,
            fallbackMessage: editedAiAgentMessage.fallbackMessage ?? "",
            sender: editedAiAgentMessage.sender ?? DEFAULT_AI_AUTOMATION_TASK_SENDER,
            sentTime: dayjs()
              .set("hour", editedAiAgentMessage.sentTime.hours)
              .set("minutes", editedAiAgentMessage.sentTime.minutes),
            sentTimeConfig: {
              mode: editedAiAgentMessage.sentTime?.mode ?? "absolute",
              avoidSendingOnTheNight: editedAiAgentMessage.sentTime?.avoidSendingOnTheNight ?? false,
              calculateFullRangeOverNight: editedAiAgentMessage.sentTime?.calculateFullRangeOverNight ?? false,
            },
            scheduleTiming: editedAiAgentMessage.scheduleTiming ?? null,
          }
        : null,
    [editedAiAgentMessage],
  );

  // TRE-2072 — one event for both AI task kinds; captured after the save succeeded.
  const trackAiTaskSaved = (
    automationTask: Extract<
      ProgramAutomationScheduledTask,
      { type: TemplateType.ScheduledAiAgentMessage | TemplateType.ScheduledAiReport }
    >,
    edited: boolean,
  ) => {
    analyticsService.track("automation_ai_task_saved", {
      automationId: selectedAutomation?.id,
      taskType: automationTask.type,
      sender: automationTask.sender ?? DEFAULT_AI_AUTOMATION_TASK_SENDER,
      scheduleAnchor: automationTask.scheduleTiming?.anchor ?? ProgramAutomationScheduleAnchor.WeekDay,
      edited,
    });
  };

  const handleSubmitAiAgentMessage = async (formData: AiAgentMessageTaskFormModel) => {
    if (!selectedAutomation) {
      return;
    }

    const automationTask: ProgramAutomationScheduledTask = {
      ...(editedAiAgentMessage?.filters ? { filters: editedAiAgentMessage.filters } : {}),
      ...(editedAiAgentMessage?.filterOperator ? { filterOperator: editedAiAgentMessage.filterOperator } : {}),
      id: editedAiAgentMessage?.id ?? uuid(),
      type: TemplateType.ScheduledAiAgentMessage,
      name: formData.name,
      promptTemplate: formData.promptTemplate,
      tone: formData.tone || null,
      maxLength: formData.maxLength ?? AI_AUTOMATION_TASK_LIMITS.maxLength.default,
      upsellHint: formData.upsellHint ?? false,
      fallbackMessage: formData.fallbackMessage?.trim() || null,
      sender: formData.sender ?? DEFAULT_AI_AUTOMATION_TASK_SENDER,
      sentTime: {
        hours: formData.sentTime.get("hour"),
        minutes: formData.sentTime.get("minutes"),
        mode: formData.sentTimeConfig?.mode ?? "absolute",
        avoidSendingOnTheNight: formData.sentTimeConfig?.avoidSendingOnTheNight ?? true,
        calculateFullRangeOverNight: formData.sentTimeConfig?.calculateFullRangeOverNight ?? false,
      },
      scheduleTiming: formData.scheduleTiming ?? null,
    };

    const automation = buildScheduledTaskUpdate(automationTask, Boolean(editedAiAgentMessage));
    if (!automation) {
      return;
    }

    try {
      await dispatch(
        programAutomationActions.updateAutomationTemplate({
          automationId: selectedAutomation.id,
          automation,
        }),
      ).unwrap();
      trackAiTaskSaved(automationTask, Boolean(editedAiAgentMessage));
      setCell(null);
      closeAiAgentMessageModal();
    } catch {
      void message.error(t("saveError"));
    }
  };

  const aiReportFormModel = useMemo<AiReportTaskFormModel | null>(
    () =>
      editedAiReport
        ? {
            name: editedAiReport.name,
            days: editedAiReport.days as AiReportTaskFormModel["days"],
            fallbackMessage: editedAiReport.fallbackMessage ?? "",
            sender: editedAiReport.sender ?? DEFAULT_AI_AUTOMATION_TASK_SENDER,
            sentTime: dayjs()
              .set("hour", editedAiReport.sentTime.hours)
              .set("minutes", editedAiReport.sentTime.minutes),
            sentTimeConfig: {
              mode: editedAiReport.sentTime?.mode ?? "absolute",
              avoidSendingOnTheNight: editedAiReport.sentTime?.avoidSendingOnTheNight ?? false,
              calculateFullRangeOverNight: editedAiReport.sentTime?.calculateFullRangeOverNight ?? false,
            },
            scheduleTiming: editedAiReport.scheduleTiming ?? null,
          }
        : null,
    [editedAiReport],
  );

  const handleSubmitAiReport = async (formData: AiReportTaskFormModel) => {
    if (!selectedAutomation) {
      return;
    }

    const automationTask: ProgramAutomationScheduledTask = {
      ...(editedAiReport?.filters ? { filters: editedAiReport.filters } : {}),
      ...(editedAiReport?.filterOperator ? { filterOperator: editedAiReport.filterOperator } : {}),
      id: editedAiReport?.id ?? uuid(),
      type: TemplateType.ScheduledAiReport,
      name: formData.name,
      days: formData.days,
      fallbackMessage: formData.fallbackMessage?.trim() || null,
      sender: formData.sender ?? DEFAULT_AI_AUTOMATION_TASK_SENDER,
      sentTime: {
        hours: formData.sentTime.get("hour"),
        minutes: formData.sentTime.get("minutes"),
        mode: formData.sentTimeConfig?.mode ?? "absolute",
        avoidSendingOnTheNight: formData.sentTimeConfig?.avoidSendingOnTheNight ?? true,
        calculateFullRangeOverNight: formData.sentTimeConfig?.calculateFullRangeOverNight ?? false,
      },
      scheduleTiming: formData.scheduleTiming ?? null,
    };

    const automation = buildScheduledTaskUpdate(automationTask, Boolean(editedAiReport));
    if (!automation) {
      return;
    }

    try {
      await dispatch(
        programAutomationActions.updateAutomationTemplate({
          automationId: selectedAutomation.id,
          automation,
        }),
      ).unwrap();
      trackAiTaskSaved(automationTask, Boolean(editedAiReport));
      setCell(null);
      closeAiReportModal();
    } catch {
      void message.error(t("saveError"));
    }
  };

  const assignHabitsFormModel = useMemo<AssignHabitsTaskFormModel | null>(
    () =>
      editedAssignHabits
        ? {
            name: editedAssignHabits.name,
            habitTemplateIds: editedAssignHabits.habitTemplateIds,
            sentTime: dayjs()
              .set("hour", editedAssignHabits.sentTime.hours)
              .set("minutes", editedAssignHabits.sentTime.minutes),
            sentTimeConfig: {
              mode: editedAssignHabits.sentTime?.mode ?? "absolute",
              avoidSendingOnTheNight: editedAssignHabits.sentTime?.avoidSendingOnTheNight ?? false,
              calculateFullRangeOverNight: editedAssignHabits.sentTime?.calculateFullRangeOverNight ?? false,
            },
            scheduleTiming: editedAssignHabits.scheduleTiming ?? null,
          }
        : null,
    [editedAssignHabits],
  );

  const handleSubmitAssignHabits = (formData: AssignHabitsTaskFormModel) => {
    if (!selectedAutomation) {
      return;
    }

    const automationTask: ProgramAutomationScheduledTask = {
      id: editedAssignHabits?.id ?? uuid(),
      type: TemplateType.ScheduledAssignHabits,
      name: formData.name,
      habitTemplateIds: formData.habitTemplateIds,
      sentTime: {
        hours: formData.sentTime.get("hour"),
        minutes: formData.sentTime.get("minutes"),
        mode: formData.sentTimeConfig?.mode ?? "absolute",
        avoidSendingOnTheNight: formData.sentTimeConfig?.avoidSendingOnTheNight ?? true,
        calculateFullRangeOverNight: formData.sentTimeConfig?.calculateFullRangeOverNight ?? false,
      },
      scheduleTiming: formData.scheduleTiming ?? null,
    };

    const automation = buildScheduledTaskUpdate(automationTask, Boolean(editedAssignHabits));
    if (!automation) {
      return;
    }

    void dispatch(
      programAutomationActions.updateAutomationTemplate({
        automationId: selectedAutomation.id,
        automation,
      }),
    );

    setCell(null);
    closeAssignHabitsModal();
  };

  const enrollChallengeFormModel = useMemo<EnrollChallengeTaskFormModel | null>(
    () =>
      editedEnrollChallenge
        ? {
            name: editedEnrollChallenge.name,
            targetMode: editedEnrollChallenge.challengeTemplateId ? "template" : "challenge",
            challengeTemplateId: editedEnrollChallenge.challengeTemplateId ?? null,
            challengeId: editedEnrollChallenge.challengeId ?? null,
            sentTime: dayjs()
              .set("hour", editedEnrollChallenge.sentTime.hours)
              .set("minutes", editedEnrollChallenge.sentTime.minutes),
            sentTimeConfig: {
              mode: editedEnrollChallenge.sentTime?.mode ?? "absolute",
              avoidSendingOnTheNight: editedEnrollChallenge.sentTime?.avoidSendingOnTheNight ?? false,
              calculateFullRangeOverNight: editedEnrollChallenge.sentTime?.calculateFullRangeOverNight ?? false,
            },
            scheduleTiming: editedEnrollChallenge.scheduleTiming ?? null,
          }
        : null,
    [editedEnrollChallenge],
  );

  const handleSubmitEnrollChallenge = (formData: EnrollChallengeTaskFormModel) => {
    if (!selectedAutomation) {
      return;
    }

    // Exactly one target: the mode radio decides which id is persisted, the other is nulled.
    const automationTask: ProgramAutomationScheduledTask = {
      id: editedEnrollChallenge?.id ?? uuid(),
      type: TemplateType.ScheduledEnrollChallenge,
      name: formData.name,
      challengeTemplateId: formData.targetMode === "template" ? (formData.challengeTemplateId ?? null) : null,
      challengeId: formData.targetMode === "challenge" ? (formData.challengeId ?? null) : null,
      sentTime: {
        hours: formData.sentTime.get("hour"),
        minutes: formData.sentTime.get("minutes"),
        mode: formData.sentTimeConfig?.mode ?? "absolute",
        avoidSendingOnTheNight: formData.sentTimeConfig?.avoidSendingOnTheNight ?? true,
        calculateFullRangeOverNight: formData.sentTimeConfig?.calculateFullRangeOverNight ?? false,
      },
      scheduleTiming: formData.scheduleTiming ?? null,
    };

    const automation = buildScheduledTaskUpdate(automationTask, Boolean(editedEnrollChallenge));
    if (!automation) {
      return;
    }

    void dispatch(
      programAutomationActions.updateAutomationTemplate({
        automationId: selectedAutomation.id,
        automation,
      }),
    );

    setCell(null);
    closeEnrollChallengeModal();
  };

  const addNewWeek = () => {
    if (!selectedAutomation) {
      return;
    }
    const nextWeek = Number(last(Object.keys(selectedAutomation?.schedule ?? {})) ?? 0) + 1;
    void dispatch(
      programAutomationActions.updateAutomationTemplate({
        automationId: selectedAutomation.id,
        automation: {
          schedule: {
            ...selectedAutomation.schedule,
            [isNaN(nextWeek) ? 1 : nextWeek]: createAutomationWeekTemplate(),
          },
        },
      }),
    );
  };

  const duplicateRow = (selectedWeek: string) => {
    if (!selectedAutomation) {
      return;
    }
    const nextWeek = Number(last(Object.keys(selectedAutomation?.schedule ?? {})) ?? 0) + 1;
    void dispatch(
      programAutomationActions.updateAutomationTemplate({
        automationId: selectedAutomation.id,
        automation: {
          schedule: {
            ...selectedAutomation.schedule,
            [nextWeek]: createAutomationWeekTemplate(selectedAutomation.schedule[selectedWeek]),
          },
        },
      }),
    );
  };

  const removeRow = (selectedWeek: string) => {
    if (!selectedAutomation) {
      return;
    }
    void dispatch(
      programAutomationActions.updateAutomationTemplate({
        automationId: selectedAutomation.id,
        automation: {
          schedule: omit(selectedAutomation.schedule, [selectedWeek]),
        },
      }),
    );
  };

  const schedule = useMemo(() => {
    if (!selectedAutomation?.schedule) {
      return [];
    }

    const plannedWeekNumber = Object.keys(selectedAutomation.schedule).length;
    const scheduledSurveysCycle: (ScheduledSurveyAutomationPlanned & {
      dayKey: string;
      weekKey: number;
    })[] = [];

    // a disabled AI window is never provisioned, so it stays out of the grid
    const aiTrainingPrograms = toAiTrainingPrograms(selectedAutomation).filter((config) => config.enabled);
    const aiNutritionPlans = toAiNutritionPlans(selectedAutomation).filter((config) => config.enabled);

    const enhancedWithWorkouts = Object.entries(cloneDeep(selectedAutomation.schedule)).map(([key, values]) => {
      const weekNumber = Number(key);

      // package-anchored tasks are listed above the table, not in the week/day grid
      Object.values(values).forEach((day) => {
        if (day) {
          day.items = day.items.filter((item) => !isPackageAnchoredTiming(item.scheduleTiming));
        }
      });

      const planForWeek = selectedAutomation.scheduledWorkoutsRoutine.find(
        (routine) => routine.startWeek <= weekNumber && routine.endWeek >= weekNumber,
      );

      const nutritionPlanForWeek = selectedAutomation.scheduledNutritionPlan?.find(
        (routine) => routine.startWeek === weekNumber,
      );

      const scheduleForWeek = planForWeek
        ? ProgramService.getScheduleForProgramAutomationWeek(planForWeek, weekNumber)
        : null;

      const workoutsById = planForWeek
        ? keyBy(
            [...planForWeek.programWorkouts.workouts, ...(planForWeek.programWorkouts.archivedWorkouts ?? [])],
            "id",
          )
        : {};

      const plannedSurveys = Object.keys(values)
        .map((dayKey) =>
          values[dayKey as unknown as WeekdaysNumber]?.items
            .map((item) => ({ ...item, dayKey }))
            .filter(
              (task): task is ScheduledSurveyAutomation & { dayKey: string } =>
                task.type === TemplateType.ScheduledSurvey,
            ),
        )
        .flat();

      plannedSurveys.forEach((item) => {
        if (!item) {
          return;
        }
        if (item.survey.type === SurveyType.OneTime) {
          return;
        }

        if (item.survey.interval.cycle.type === CycleSurveyType.CycleNumber) {
          const cycleNumber = item.survey.interval.cycle.numberOfCycles - 1;
          Array.from({ length: cycleNumber }).forEach((_, i) => {
            const nextWeek = weekNumber + (item.survey.interval?.count ?? 1) * (i + 1);

            if (nextWeek > plannedWeekNumber) {
              return;
            }

            scheduledSurveysCycle.push({
              ...item,
              cycleNumber: 1 + (i + 1),
              source: item.id,
              id: uuid(),
              dayKey: item.dayKey,
              weekKey: nextWeek,
            });
          });
        }
        if (item.survey.interval.cycle.type === CycleSurveyType.Infinite) {
          const cycleNumber = plannedWeekNumber - weekNumber;
          Array.from({ length: cycleNumber }).forEach((_, i) => {
            const nextWeek = weekNumber + (item.survey.interval?.count ?? 1) * (i + 1);

            if (nextWeek > plannedWeekNumber) {
              return;
            }

            scheduledSurveysCycle.push({
              ...item,
              id: uuid(),
              dayKey: item.dayKey,
              weekKey: nextWeek,
              cycleNumber: 1 + (i + 1),
              source: item.id,
            });
          });
        }
      });

      return {
        ...Object.fromEntries(
          Object.entries(values).map(([dayKey, day]) => {
            const dayToString = weekdaysArr[Number(dayKey)] as WeekDay;
            const workouts = scheduleForWeek ? scheduleForWeek.week?.[dayToString] : null;

            return [
              dayKey,
              {
                ...day,
                workouts: workouts
                  ? Object.values(workouts)
                      .map((workoutId: string) => workoutsById[workoutId])
                      .filter((item) => item)
                  : [],
              },
            ];
          }),
        ),
        week: key,
        scheduleForWeek,
        nutritionPlanForWeek: nutritionPlanForWeek || null,
        // a generated program covers its whole window, so every week in it is marked
        aiTrainingProgramsForWeek: aiTrainingPrograms.filter((config) => isWeekInAiWindow(config, weekNumber)),
        // a diet is generated at the start of its window (legacy rows may also carry
        // `aiGenerated` regeneration entries in scheduledNutritionPlan — nutritionPlanForWeek above)
        aiNutritionPlansForWeek: aiNutritionPlans.filter((config) => config.startWeek === weekNumber),
      };
    });

    const grouped = groupBy(scheduledSurveysCycle, "weekKey");

    Object.keys(grouped).forEach((weekStr) => {
      const weekNumber = Number(weekStr) - 1;

      const scheduledSurveys = grouped[weekStr] || [];

      scheduledSurveys.forEach((item) => {
        // @ts-expect-error ignore
        enhancedWithWorkouts[weekNumber][item.dayKey]?.items.push(item);
      });
    });

    return enhancedWithWorkouts;
  }, [selectedAutomation]);

  const packageAnchoredTasks = useMemo(() => {
    if (!selectedAutomation) {
      return [];
    }

    const tasks: (
      | { item: ProgramAutomationScheduledTask; source: "bag" }
      | {
          item: ProgramAutomationScheduledTask;
          source: "cell";
          week: string;
          dayNumber: WeekdayNumberToString;
          dayId: string;
        }
    )[] = (selectedAutomation.packageAnchoredTasks ?? []).map((item) => ({ item, source: "bag" }));

    // legacy entries saved into a week/day cell before the dedicated bag existed
    Object.entries(selectedAutomation.schedule ?? {}).forEach(([week, days]) => {
      Object.entries(days).forEach(([dayNumber, day]) => {
        day?.items?.forEach((item) => {
          if (isPackageAnchoredTiming(item.scheduleTiming)) {
            tasks.push({ item, source: "cell", week, dayNumber: dayNumber as WeekdayNumberToString, dayId: day.id });
          }
        });
      });
    });

    return tasks;
  }, [selectedAutomation]);

  const removePackageAnchoredTask = (itemId: string) => {
    if (!selectedAutomation) {
      return;
    }

    void dispatch(
      programAutomationActions.updateAutomationTemplate({
        automationId: selectedAutomation.id,
        automation: {
          packageAnchoredTasks: (selectedAutomation.packageAnchoredTasks ?? []).filter((task) => task.id !== itemId),
        },
      }),
    );
  };

  const renderAutomationItem = (
    item: ProgramAutomationScheduledTask,
    dayId: string,
    dayNumber: WeekdayNumberToString,
    week: string,
  ) => {
    if (item.type === TemplateType.ScheduledMessages) {
      return (
        <MessageAutomationItem
          key={`${item.type}-${item.id}`}
          item={item}
          onEdit={() => setEditedModelForAutomatedMessages(item, { day: dayNumber, week })}
          onRemove={() => removeAddedAutomation(item.id, dayId, dayNumber, week)}
        />
      );
    }

    if (item.type === TemplateType.ScheduledSurvey) {
      return (
        <SurveyAutomationItem
          key={`${item.type}-${item.id}`}
          item={item}
          onEdit={() => {
            // setEditedModelForAutomatedMessages(item, dayNumber, week)
          }}
          onRemove={() => {
            removeAddedAutomation(item.id, dayId, dayNumber, week);
          }}
        />
      );
    }

    if (item.type === TaskType.EmailClient) {
      return (
        <EmailClientTaskItem
          key={`${item.type}-${item.id}`}
          item={item}
          onRemove={() => removeAddedAutomation(item.id, dayId, dayNumber, week)}
        />
      );
    }

    if (item.type === TemplateType.ScheduledPopUpFromAutomation) {
      return (
        <PopUpAutomationItem
          key={`${item.type}-${item.id}`}
          item={item}
          onRemove={() => removeAddedAutomation(item.id, dayId, dayNumber, week)}
          onEdit={() => {
            setCell({ day: dayNumber, week });
            setEditedModelForCampaignPopup(item);
          }}
        />
      );
    }

    if (item.type === TaskType.FitnessApp) {
      return (
        <FitnessAppTaskItem
          key={`${item.type}-${item.id}`}
          item={item}
          onRemove={() => removeAddedAutomation(item.id, dayId, dayNumber, week)}
        />
      );
    }

    if (item.type === TaskType.TeamMemberTask) {
      return (
        <TeamMemberTaskItem
          key={`${item.type}-${item.id}`}
          item={item}
          onRemove={() => removeAddedAutomation(item.id, dayId, dayNumber, week)}
        />
      );
    }

    if (item.type === TemplateType.ScheduledAiAgentMessage) {
      return (
        <AiAgentMessageItem
          key={`${item.type}-${item.id}`}
          item={item}
          onEdit={() => {
            setCell({ day: dayNumber, week });
            openAiAgentMessageModal(item);
          }}
          onRemove={() => removeAddedAutomation(item.id, dayId, dayNumber, week)}
        />
      );
    }

    if (item.type === TemplateType.ScheduledAiReport) {
      return (
        <AiReportItem
          key={`${item.type}-${item.id}`}
          item={item}
          onEdit={() => {
            setCell({ day: dayNumber, week });
            openAiReportModal(item);
          }}
          onRemove={() => removeAddedAutomation(item.id, dayId, dayNumber, week)}
        />
      );
    }

    if (item.type === TemplateType.ScheduledAssignHabits) {
      return (
        <AssignHabitsItem
          key={`${item.type}-${item.id}`}
          item={item}
          onEdit={() => {
            setCell({ day: dayNumber, week });
            openAssignHabitsModal(item);
          }}
          onRemove={() => removeAddedAutomation(item.id, dayId, dayNumber, week)}
        />
      );
    }

    if (item.type === TemplateType.ScheduledEnrollChallenge) {
      return (
        <EnrollChallengeItem
          key={`${item.type}-${item.id}`}
          item={item}
          onEdit={() => {
            setCell({ day: dayNumber, week });
            openEnrollChallengeModal(item);
          }}
          onRemove={() => removeAddedAutomation(item.id, dayId, dayNumber, week)}
        />
      );
    }

    return null;
  };

  const renderPackageAnchoredItem = (item: ProgramAutomationScheduledTask) => {
    if (item.type === TemplateType.ScheduledMessages) {
      return (
        <MessageAutomationItem
          key={`${item.type}-${item.id}`}
          item={item}
          onEdit={() => setEditedModelForAutomatedMessages(item, null)}
          onRemove={() => removePackageAnchoredTask(item.id)}
        />
      );
    }

    if (item.type === TemplateType.ScheduledSurvey) {
      return (
        <SurveyAutomationItem
          key={`${item.type}-${item.id}`}
          item={item}
          onEdit={() => {}}
          onRemove={() => removePackageAnchoredTask(item.id)}
        />
      );
    }

    if (item.type === TaskType.EmailClient) {
      return (
        <EmailClientTaskItem
          key={`${item.type}-${item.id}`}
          item={item}
          onRemove={() => removePackageAnchoredTask(item.id)}
        />
      );
    }

    if (item.type === TemplateType.ScheduledPopUpFromAutomation) {
      return (
        <PopUpAutomationItem
          key={`${item.type}-${item.id}`}
          item={item}
          onRemove={() => removePackageAnchoredTask(item.id)}
          onEdit={() => {
            setCell(null);
            setEditedModelForCampaignPopup(item);
          }}
        />
      );
    }

    if (item.type === TaskType.FitnessApp) {
      return (
        <FitnessAppTaskItem
          key={`${item.type}-${item.id}`}
          item={item}
          onRemove={() => removePackageAnchoredTask(item.id)}
        />
      );
    }

    if (item.type === TaskType.TeamMemberTask) {
      return (
        <TeamMemberTaskItem
          key={`${item.type}-${item.id}`}
          item={item}
          onRemove={() => removePackageAnchoredTask(item.id)}
        />
      );
    }

    if (item.type === TemplateType.ScheduledAiAgentMessage) {
      return (
        <AiAgentMessageItem
          key={`${item.type}-${item.id}`}
          item={item}
          onEdit={() => {
            setCell(null);
            openAiAgentMessageModal(item);
          }}
          onRemove={() => removePackageAnchoredTask(item.id)}
        />
      );
    }

    if (item.type === TemplateType.ScheduledAiReport) {
      return (
        <AiReportItem
          key={`${item.type}-${item.id}`}
          item={item}
          onEdit={() => {
            setCell(null);
            openAiReportModal(item);
          }}
          onRemove={() => removePackageAnchoredTask(item.id)}
        />
      );
    }

    if (item.type === TemplateType.ScheduledAssignHabits) {
      return (
        <AssignHabitsItem
          key={`${item.type}-${item.id}`}
          item={item}
          onEdit={() => {
            setCell(null);
            openAssignHabitsModal(item);
          }}
          onRemove={() => removePackageAnchoredTask(item.id)}
        />
      );
    }

    if (item.type === TemplateType.ScheduledEnrollChallenge) {
      return (
        <EnrollChallengeItem
          key={`${item.type}-${item.id}`}
          item={item}
          onEdit={() => {
            setCell(null);
            openEnrollChallengeModal(item);
          }}
          onRemove={() => removePackageAnchoredTask(item.id)}
        />
      );
    }

    return null;
  };

  const renderCell = useCallback(
    (day: DayContent & { workouts?: ProgramWorkout[] }, row: AutomationRow, rowIndex: number, dayNumber: number) => {
      const addButton = (
        <AddAutomationButton
          addScheduledMessages={() => openScheduleMessagesForm(`${dayNumber}` as WeekdayNumberToString, row.week)}
          addEmailAction={() => openScheduleEmailAction(`${dayNumber}` as WeekdayNumberToString, row.week)}
          addSurveyAction={() => openSurveyForm(`${dayNumber}` as WeekdayNumberToString, row.week)}
          addPopUpAction={() => openPopUpForm(`${dayNumber}` as WeekdayNumberToString, row.week)}
          addTagsAction={() => openTagsActionForm(`${dayNumber}` as WeekdayNumberToString, row.week)}
          addTeamMemberTask={() => openTeamMemberTaskForm(`${dayNumber}` as WeekdayNumberToString, row.week)}
          addAiAgentMessage={() => openAiAgentMessageForm(`${dayNumber}` as WeekdayNumberToString, row.week)}
          addAiReport={() => openAiReportForm(`${dayNumber}` as WeekdayNumberToString, row.week)}
          addAssignHabits={() => openAssignHabitsForm(`${dayNumber}` as WeekdayNumberToString, row.week)}
          addEnrollChallenge={() => openEnrollChallengeForm(`${dayNumber}` as WeekdayNumberToString, row.week)}
        />
      );

      // plan/diet markers live in the day-1 cell of their week
      const planMarkers =
        dayNumber === 1
          ? [
              ...row.aiTrainingProgramsForWeek.map((config) => (
                <AiPlanMarker
                  key={`ai-training-program-${config.id}`}
                  muted={config.startWeek !== Number(row.week)}
                  label={
                    config.startWeek === Number(row.week)
                      ? "Plan treningowy AI — generowany agentem"
                      : "Plan treningowy AI"
                  }
                  tooltip={`Plan generowany agentem na podstawie ${config.referenceProgramIds.length} plan(ów) referencyjnych i ankiety klientki (${aiWeeksLabel(
                    config,
                  )}).`}
                />
              )),
              ...row.aiNutritionPlansForWeek.map((config) => (
                <AiPlanMarker
                  key={`ai-nutrition-plan-${config.id}`}
                  label="Dieta AI — generowana agentem"
                  tooltip={`Dieta generowana agentem (${config.mealsSchema} posiłki, model ${
                    config.model
                  }, ${aiWeeksLabel(config)}).`}
                />
              )),
              row.nutritionPlanForWeek ? (
                <div key="nutrition-plan">
                  <Utensils color="#2699fb" size={16} />{" "}
                  <span>
                    {row.nutritionPlanForWeek.type === ScheduledNutritionType.AiGenerated
                      ? "Dieta AI — okno regeneracji"
                      : t("nutrition.addPlannedNutrition")}
                  </span>
                </div>
              ) : null,
            ].filter(Boolean)
          : [];

      return day.items?.length || day.workouts?.length || planMarkers.length ? (
        <Space direction="vertical" key={`${rowIndex}-${dayNumber}`}>
          {day.workouts?.map((workout) => (
            <Space size={2} key={workout.id}>
              <Badge status="processing" /> {workout.name}
            </Space>
          ))}
          {planMarkers}

          {sortBy(day.items, ["sentTime.hours", "sentTime.minutes"]).map((item) =>
            renderAutomationItem(item, day.id, `${dayNumber}` as WeekdayNumberToString, row.week),
          )}

          {addButton}
        </Space>
      ) : (
        addButton
      );
    },
    [selectedAutomation],
  );

  const openScheduleMessagesForm = (day: WeekdayNumberToString, week: string) => {
    setCell({ day, week });
    toggleAutomatedMessagesForm(true);
  };

  // "Dodaj zadanie" in the package-tasks card - no source cell, forms preset a package anchor
  const packageTaskMenuItems = [
    {
      key: "message",
      label: t("addAutomationMessage"),
      onClick: () => {
        setCell(null);
        toggleAutomatedMessagesForm(true);
      },
    },
    {
      key: "email",
      label: t("programAutomation.addEmailAction"),
      onClick: () => {
        setCell(null);
        openTaskModal();
      },
    },
    {
      key: "survey",
      label: t("programAutomation.addSurveyAction"),
      onClick: () => {
        setCell(null);
        toggleSurveyForm(true);
      },
    },
    {
      key: "popup",
      label: t("programAutomation.addPopUpAction"),
      onClick: () => {
        setCell(null);
        toggleCampaignPopupForm(true);
      },
    },
    {
      key: "tags",
      label: t("programAutomation.addTagsAction"),
      onClick: () => {
        setCell(null);
        openFitnessAppTaskModal();
      },
    },
    {
      key: "teamMemberTask",
      label: t("programAutomation.addTeamMemberTask"),
      onClick: () => {
        setCell(null);
        openTeamMemberTaskModal();
      },
    },
    {
      key: "aiAgentMessage",
      label: t("automation:programAutomation.aiAgentMessageTask"),
      onClick: () => {
        setCell(null);
        openAiAgentMessageModal();
      },
    },
    {
      key: "aiReport",
      label: t("automation:programAutomation.aiReportTask"),
      onClick: () => {
        setCell(null);
        openAiReportModal();
      },
    },
    {
      key: "assignHabits",
      label: "Przypisanie nawyków",
      onClick: () => {
        setCell(null);
        openAssignHabitsModal();
      },
    },
    {
      key: "enrollChallenge",
      label: "Zapis do wyzwania",
      onClick: () => {
        setCell(null);
        openEnrollChallengeModal();
      },
    },
  ];

  const addPackageTaskButton = (
    <Dropdown menu={{ items: packageTaskMenuItems }} trigger={["click"]}>
      <Button type="primary" ghost icon={<PlusOutlined />}>
        Dodaj zadanie
      </Button>
    </Dropdown>
  );

  const openScheduleEmailAction = (day: WeekdayNumberToString, week: string) => {
    setCell({ day, week });
    openTaskModal();
  };

  const openSurveyForm = (day: WeekdayNumberToString, week: string) => {
    setCell({ day, week });
    toggleSurveyForm(true);
  };

  const openPopUpForm = (day: WeekdayNumberToString, week: string) => {
    setCell({ day, week });
    toggleCampaignPopupForm(true);
  };

  const openTagsActionForm = (day: WeekdayNumberToString, week: string) => {
    setCell({ day, week });
    openFitnessAppTaskModal();
  };

  const openTeamMemberTaskForm = (day: WeekdayNumberToString, week: string) => {
    setCell({ day, week });
    openTeamMemberTaskModal();
  };

  const openAiAgentMessageForm = (day: WeekdayNumberToString, week: string) => {
    setCell({ day, week });
    openAiAgentMessageModal();
  };

  const openAiReportForm = (day: WeekdayNumberToString, week: string) => {
    setCell({ day, week });
    openAiReportModal();
  };

  const openAssignHabitsForm = (day: WeekdayNumberToString, week: string) => {
    setCell({ day, week });
    openAssignHabitsModal();
  };

  const openEnrollChallengeForm = (day: WeekdayNumberToString, week: string) => {
    setCell({ day, week });
    openEnrollChallengeModal();
  };

  const removeAddedAutomation = (itemId: string, dayId: string, dayNumber: WeekdayNumberToString, week: string) => {
    if (!selectedAutomation) {
      return;
    }

    const weekToUpdate = cloneDeep(selectedAutomation.schedule[week]) || {};

    weekToUpdate[dayNumber] = {
      id: weekToUpdate[dayNumber]?.id ?? uuid(),
      items: weekToUpdate[dayNumber]?.items?.filter((item) => item.id !== itemId) || [],
    };

    void dispatch(
      programAutomationActions.updateAutomationTemplate({
        automationId: selectedAutomation.id,
        automation: {
          schedule: {
            ...selectedAutomation.schedule,
            [week]: weekToUpdate,
          },
        },
      }),
    );
  };

  return (
    <>
      <Card
        size="small"
        title="Zadania względem pakietu"
        className="mt-8"
        extra={packageAnchoredTasks.length ? addPackageTaskButton : null}
      >
        {packageAnchoredTasks.length ? (
          <Space direction="vertical" size="middle" className="w-full">
            {packageAnchoredTasks.map((entry) => (
              <Space key={`${entry.item.type}-${entry.item.id}`} align="start" wrap>
                <Tag color="blue">
                  {isPackageAnchoredTiming(entry.item.scheduleTiming)
                    ? getScheduleTimingLabel(entry.item.scheduleTiming)
                    : null}
                </Tag>
                {entry.source === "bag"
                  ? renderPackageAnchoredItem(entry.item)
                  : renderAutomationItem(entry.item, entry.dayId, entry.dayNumber, entry.week)}
              </Space>
            ))}
          </Space>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Brak zadań względem pakietu, np. X dni przed końcem lub w procencie trwania pakietu"
          >
            {addPackageTaskButton}
          </Empty>
        )}
      </Card>
      <Table
        dataSource={schedule}
        pagination={false}
        bordered
        rowKey="week"
        loading={itemStatus === RequestStatus.FETCHING}
        scroll={{ x: true }}
        className="mb-4 mt-8"
      >
        <Table.Column<AutomationRow>
          title={t("programAutomation.weekHeader")}
          dataIndex="week"
          width={200}
          key="week"
          render={(_, row, i) => {
            return (
              <Space size={2}>
                <span className="whitespace-nowrap">{t("programAutomation.week", { week: row.week })}</span>
                <Button
                  title={t<string>("common:button.duplicate")}
                  type="link"
                  icon={<CopyOutlined />}
                  onClick={() => duplicateRow(row.week)}
                />
                {i > 0 && (
                  <Button
                    title={t<string>("common:button.delete")}
                    danger
                    type="link"
                    icon={<DeleteOutlined />}
                    onClick={() => removeRow(row.week)}
                  />
                )}
              </Space>
            );
          }}
        />
        {weekdays.map((_, i) => {
          return (
            <Table.Column<AutomationRow>
              title={t("programAutomation.day", { day: i + 1 })}
              dataIndex={i + 1}
              key={i + 1}
              /* eslint-disable-next-line @typescript-eslint/no-unsafe-argument */
              render={(...args) => renderCell(...args, i + 1)}
              // width="11%"
            />
          );
        })}
      </Table>

      <Button type="primary" icon={<PlusOutlined />} onClick={addNewWeek}>
        {t("programAutomation.addWeek")}
      </Button>
      <ModalForm<AutomatedMessagesFormModel>
        open={showAutomatedMessagesForm}
        onCancel={() => {
          toggleAutomatedMessagesForm(false);
          setModel(null);
          setCell(null);
        }}
        loading={loading}
        title={model ? t("programAutomation.form.update") : t("programAutomation.form.title")}
        okText={model ? t("programAutomation.update") : t("programAutomation.add")}
      >
        <AutomatedMessagesForm
          onSubmit={handleSubmit}
          model={model || { mode: FormMode.Template_Add, ...(selectedCell ? {} : PACKAGE_TASK_PRESET) }}
          automationMode
        />
      </ModalForm>

      <ModalForm<ProgramAutomationTaskFormModel>
        open={automationTaskFormVisible}
        onCancel={() => {
          setCell(null);
          closeTaskModal();
        }}
        title={t("automationTasks.title")}
        width={900}
      >
        <ProgramAutomationTaskForm
          formController={programAutomationTaskFormController}
          onSubmit={handleSubmitEmailTask}
          model={editedTask ?? (selectedCell ? undefined : PACKAGE_TASK_PRESET)}
        />
      </ModalForm>

      <ModalForm
        open={showSurveyForm}
        onCancel={() => {
          setCell(null);
          toggleSurveyForm(false);
        }}
        title={t("surveys:addSurvey")}
        width={900}
      >
        <AddSurveyTemplateForm
          fromAutomation
          onSubmit={onSendSurveyForm}
          selectedCell={selectedCell}
          model={selectedCell ? undefined : PACKAGE_TASK_PRESET}
        />
      </ModalForm>

      <ModalForm<CampaignMessageComposeFormModel>
        open={showCampaignPopupForm}
        onCancel={() => {
          toggleCampaignPopupForm(false);
          campaignFormController.resetFields();
          setCell(null);
        }}
        loading={campaignLoading}
        title={t("campaigns:popup.title")}
        okText={campaignModel ? t("common:button.update") : t("common:button.add")}
        width={900}
      >
        <ComposeCampaignForm
          allowSelectOnlyTime
          formController={campaignFormController}
          onSubmit={handleCampaignSubmit}
          model={campaignModel ?? (selectedCell ? undefined : PACKAGE_TASK_PRESET)}
        />
      </ModalForm>

      <ModalForm<FitnessAppTaskFormModel>
        open={fitnessAppTaskFormVisible}
        onCancel={() => {
          setCell(null);
          closeFitnessAppTaskModal();
        }}
        title={t("programAutomation.tagsAction")}
        width={900}
      >
        <FitnessAppTaskForm
          formController={fitnessAppTaskFormController}
          onSubmit={handleSubmitTagsAction}
          model={selectedCell ? undefined : PACKAGE_TASK_PRESET}
        />
      </ModalForm>

      <ModalForm<TeamMemberTaskFormModel>
        open={teamMemberTaskFormVisible}
        onCancel={() => {
          setCell(null);
          closeTeamMemberTaskModal();
        }}
        title={t("programAutomation.addTeamMemberTask")}
        width={900}
      >
        <TeamMemberTaskForm
          formController={teamMemberTaskFormController}
          onSubmit={handleSubmitTeamMemberTask}
          model={selectedCell ? undefined : PACKAGE_TASK_PRESET}
        />
      </ModalForm>

      <ModalForm<AiAgentMessageTaskFormModel>
        open={aiAgentMessageFormVisible}
        onCancel={() => {
          setCell(null);
          closeAiAgentMessageModal();
        }}
        title={
          editedAiAgentMessage
            ? t("automation:programAutomation.aiReport.editTitle", {
                task: t("automation:programAutomation.aiAgentMessageTask"),
              })
            : t("automation:programAutomation.addAiAgentMessageAction")
        }
        okText={editedAiAgentMessage ? t("common:button.update") : t("common:button.add")}
        width={900}
      >
        <AiAgentMessageTaskForm
          formController={aiAgentMessageFormController}
          onSubmit={handleSubmitAiAgentMessage}
          model={aiAgentMessageFormModel ?? (selectedCell ? undefined : PACKAGE_TASK_PRESET)}
          advisorEnabled={selectedAutomation?.advisorEnabled}
        />
      </ModalForm>

      <ModalForm<AiReportTaskFormModel>
        open={aiReportFormVisible}
        onCancel={() => {
          setCell(null);
          closeAiReportModal();
        }}
        title={
          editedAiReport
            ? t("automation:programAutomation.aiReport.editTitle", {
                task: t("automation:programAutomation.aiReportTask"),
              })
            : t("automation:programAutomation.addAiReportAction")
        }
        okText={editedAiReport ? t("common:button.update") : t("common:button.add")}
        width={900}
      >
        <AiReportTaskForm
          formController={aiReportFormController}
          onSubmit={handleSubmitAiReport}
          model={aiReportFormModel ?? (selectedCell ? undefined : PACKAGE_TASK_PRESET)}
          advisorEnabled={selectedAutomation?.advisorEnabled}
        />
      </ModalForm>

      <ModalForm<AssignHabitsTaskFormModel>
        open={assignHabitsFormVisible}
        onCancel={() => {
          setCell(null);
          closeAssignHabitsModal();
        }}
        title={editedAssignHabits ? "Edytuj przypisanie nawyków" : "Dodaj przypisanie nawyków"}
        okText={editedAssignHabits ? t("common:button.update") : t("common:button.add")}
        width={900}
      >
        <AssignHabitsTaskForm
          formController={assignHabitsFormController}
          onSubmit={handleSubmitAssignHabits}
          model={assignHabitsFormModel ?? (selectedCell ? undefined : PACKAGE_TASK_PRESET)}
        />
      </ModalForm>

      <ModalForm<EnrollChallengeTaskFormModel>
        open={enrollChallengeFormVisible}
        onCancel={() => {
          setCell(null);
          closeEnrollChallengeModal();
        }}
        title={editedEnrollChallenge ? "Edytuj zapis do wyzwania" : "Dodaj zapis do wyzwania"}
        okText={editedEnrollChallenge ? t("common:button.update") : t("common:button.add")}
        width={900}
      >
        <EnrollChallengeTaskForm
          formController={enrollChallengeFormController}
          onSubmit={handleSubmitEnrollChallenge}
          model={enrollChallengeFormModel ?? (selectedCell ? undefined : PACKAGE_TASK_PRESET)}
        />
      </ModalForm>
    </>
  );
};

export default memo(AutomationScheduleTable);
